import {
  Asset,
  AssetRepository,
  GetAssetByIdOrSlugParams,
  GetAssetsParams,
  GetAssetsResponse,
  GetFacetsParams,
  Meta,
  DownloadsRankingPeriod,
} from '../types'
import {
  assetTypeToMeilisearchVertical,
  mainCategorySlugToMeilisearchVertical,
  meilisearchAssetToAsset,
} from '@/lib/meilisearch/utils'
import { ITEMS_PER_PAGE, MAX_ASSETS_FILTERS_TAGS } from '@/lib/constants'
import { MEILISEARCH_ASSETS_INDEX, PER_PAGE_ORDERING, features } from '@/lib/config'
import client from '@/lib/meilisearch/client'
import {
  Category,
  Content,
  getCategoryByIdOrSlug,
  getContentByIdOrSlug,
  getContents,
  getMainCategoryByIdOrSlug,
  getSubCategoryByIdOrSlug,
} from '@/lib/categories/categories'
import { buildPageKey, buildSortQuery } from './page-sort'
import { buildTextQuery } from '@/lib/search/search-translation'
import { FederatedMultiSearchParams, Filter, MultiSearchQuery } from 'meilisearch'
import { commonConnectors, SortFilterValue } from '@/lib/filters/filters'
import { measureSearchRequest, measureWebappLoad, type WebappLoadTimings } from '@/lib/analytics/load-performance'
import { getRefiningTags } from './refining-tags'
import { getFacetScope } from './facet-scope'

const indexName = MEILISEARCH_ASSETS_INDEX
const SEARCH_VERTICAL_FILTER = 'vertical IN ["videos", "sounds", "graphics", "3d"]'

type ComposedQuery = {
  query: MultiSearchQuery
  facetFilter: Filter
}

export class MeilisearchAssetRepository implements AssetRepository {
  async getSearchVerticalCounts({
    search,
    premium,
    level,
    tags,
    signal,
  }: Pick<GetAssetsParams, 'search' | 'premium' | 'level' | 'tags' | 'signal'>): Promise<Record<string, number>> {
    const [composed] = await this.buildQueries({ search, premium, level, tags })
    const result = await client.index(indexName).search(
      composed.query.q,
      {
        filter: [
          SEARCH_VERTICAL_FILTER,
          ...(Array.isArray(composed.query.filter)
            ? composed.query.filter
            : composed.query.filter
              ? [composed.query.filter]
              : []),
        ],
        facets: ['vertical'],
        limit: 0,
        attributesToRetrieve: [],
      },
      { signal }
    )
    const counts = result.facetDistribution?.vertical ?? {}
    const byCategory = {
      vfx: counts.videos ?? 0,
      sfx: counts.sounds ?? 0,
      graphics: counts.graphics ?? 0,
      '3d': counts['3d'] ?? 0,
    }
    return { ...byCategory, all: Object.values(byCategory).reduce((total, count) => total + count, 0) }
  }

  async getAssets(params: GetAssetsParams): Promise<GetAssetsResponse> {
    const timings: WebappLoadTimings = { service: 'meilisearch' }
    const filtersActive =
      params.premium !== undefined ||
      params.level !== undefined ||
      params.popularityRankingPeriod !== undefined ||
      Boolean(params.tags?.length)

    return measureWebappLoad(
      {
        surface: 'assets',
        operation:
          (params.page ?? 1) > 1 ? 'pagination' : params.search ? 'search' : filtersActive ? 'filter' : 'browse',
        main_category: params.mainCategorySlug ?? undefined,
        category: params.categorySlug ?? undefined,
        sub_category: params.subCategorySlug ?? undefined,
        content: params.contentSlug ?? undefined,
        page: params.page ?? 1,
        filters_active: filtersActive,
      },
      () => this.queryAssets(params, timings),
      (response) => response.data.length,
      { signal: params.signal, getTimings: () => timings }
    )
  }

  private async queryAssets(
    {
      mainCategorySlug,
      categorySlug,
      subCategorySlug,
      contentSlug,
      page,
      search,
      premium,
      level,
      popularityRankingPeriod,
      sort,
      tags,
      signal,
      contentsLimit,
      includeMainCategoryVerticals,
    }: GetAssetsParams,
    timings: WebappLoadTimings
  ): Promise<GetAssetsResponse> {
    const preparedAt = performance.now()
    // Category tags strategy:
    // The strategy is to match the category path formed by the main category, category, subCategory and content slugs
    // and try to match the assets that belong to that path using the `categories_path` attribute
    // const categoryTags = [mainCategorySlug, categorySlug,
    // subCategorySlug, contentSlug].filter(Boolean).join('/')
    // const categoryFilters = ['category_tags EXISTS', `category_tags IN ["${categoryTags}"]`]

    const limit = ITEMS_PER_PAGE
    const currentPage = page ?? 1
    const offset = (currentPage - 1) * ITEMS_PER_PAGE

    let totalItems = 0
    let totalPages = 0
    let hits: Asset[] = []
    let firstHitRankingScore: number | undefined
    let firstHitMatchesPosition: Record<string, unknown> | undefined

    // For listings, retrieve only the necessary attributes
    const attributesToRetrieve = [
      'id',
      'uuid',
      'marketplace',
      'vertical',
      'name',
      'slug',
      'level',
      'properties',
      'previews',
      'files',
      'downloads_ranking',
    ]

    const queries = await this.buildQueries({
      mainCategorySlug,
      categorySlug,
      subCategorySlug,
      contentSlug,
      search,
      premium,
      level,
      popularityRankingPeriod,
      sort,
      tags,
      contentsLimit,
      includeMainCategoryVerticals,
      attributesToRetrieve,
    })

    timings.preparation_ms = Math.round(performance.now() - preparedAt)
    const isMultiSearch = queries.length > 1

    if (isMultiSearch) {
      const results = await measureSearchRequest(timings, () =>
        client.multiSearch<FederatedMultiSearchParams, Asset>(
          {
            federation: {
              limit,
              offset,
              facetsByIndex: {
                [indexName]: ['tags'],
              },
            },
            // The search-translation fallback judges a typed search by its first hit's score;
            // Search Insights reads which attributes the first hit of the first page matched
            queries: queries.map(({ query }) => ({
              ...query,
              showRankingScore: !!query.q,
              showMatchesPosition: !!query.q && offset === 0,
            })),
          },
          {
            signal,
          }
        )
      )

      totalItems = results.estimatedTotalHits ?? 0
      totalPages = totalItems > 0 ? Math.ceil(totalItems / ITEMS_PER_PAGE) : 0
      hits = results.hits
      firstHitRankingScore = results.hits[0]?._rankingScore
      firstHitMatchesPosition = results.hits[0]?._matchesPosition

      // TODO: Implement facets for multiple queries
    } else {
      // If there's only one query, we can use the first one. If not, we'll use a default search query
      const resolvedQuery = queries[0]

      if (!resolvedQuery) {
        throw new Error('No query found')
      }

      const index = client.index(resolvedQuery.query.indexUid)

      const results = await measureSearchRequest(timings, () =>
        index.search<Asset>(
          resolvedQuery.query.q,
          {
            filter: resolvedQuery.query.filter,
            limit,
            offset,
            sort: resolvedQuery.query.sort,
            attributesToRetrieve: resolvedQuery.query.attributesToRetrieve,
            // The search-translation fallback judges a typed search by its first hit's score;
            // Search Insights reads which attributes the first hit of the first page matched
            showRankingScore: !!resolvedQuery.query.q,
            showMatchesPosition: !!resolvedQuery.query.q && offset === 0,
          },
          {
            signal,
          }
        )
      )

      totalItems = results.estimatedTotalHits
      totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
      hits = results.hits
      firstHitRankingScore = results.hits[0]?._rankingScore
      firstHitMatchesPosition = results.hits[0]?._matchesPosition
    }

    const assets = hits.map((asset) =>
      meilisearchAssetToAsset(asset, {
        mainCategorySlug,
        categorySlug,
        subCategorySlug,
        contentSlug,
      })
    )

    // Generate metadata
    const meta: Meta = {
      current_page: currentPage,
      total_pages: totalPages,
      total_items: totalItems,
      items_per_page: ITEMS_PER_PAGE,
    }

    console.debug('Found %d assets', hits.length)

    return {
      data: assets,
      meta,
      load_timings: timings,
      ...(firstHitRankingScore !== undefined ? { firstHitRankingScore } : {}),
      ...(firstHitMatchesPosition !== undefined ? { firstHitMatchesPosition } : {}),
    }
  }

  async getAssetByIdOrSlug(params: GetAssetByIdOrSlugParams): Promise<Asset | null> {
    const attributesToRetrieve = [
      'id',
      'uuid',
      'marketplace',
      'vertical',
      'name',
      'description',
      'slug',
      'level',
      'previews',
      'tags',
      'properties',
      'files',
    ]

    let asset: Asset | null

    try {
      asset = await client.index(indexName).getDocument<Asset>(params.assetIdOrSlug, {
        fields: attributesToRetrieve,
      })
    } catch (error) {
      asset = null
    }

    // Find first by slug if not found by id
    if (!asset) {
      const result = await client.index(indexName).search<Asset>(params.assetIdOrSlug, {
        filter: ['slug = "' + params.assetIdOrSlug + '"'],
        limit: 1,
        attributesToRetrieve,
      })

      asset = result.hits.length > 0 ? result.hits[0] : null
    }

    if (!asset) {
      return null
    }

    return meilisearchAssetToAsset(asset, {
      mainCategorySlug: params.mainCategoryIdOrSlug,
      categorySlug: params.categoryIdOrSlug,
      subCategorySlug: params.subCategoryIdOrSlug,
      contentSlug: params.contentIdOrSlug,
    })
  }

  /**
   * Gets the facets (tags) based on the provided parameters.
   * This method is decoupled from getAssets to allow independent use.
   */
  async getFacets(params: GetFacetsParams): Promise<string[]> {
    const timings: WebappLoadTimings = { service: 'meilisearch' }
    const scope = getFacetScope(params, features.compactCategoryNavigation)
    const filtersActive = scope.premium !== undefined || scope.level !== undefined

    return measureWebappLoad(
      {
        surface: 'facets',
        operation: 'filter_options',
        main_category: params.mainCategorySlug ?? undefined,
        category: params.categorySlug ?? undefined,
        sub_category: params.subCategorySlug ?? undefined,
        content: params.contentSlug ?? undefined,
        filters_active: filtersActive,
      },
      () => this.queryFacets(scope, timings),
      (facets) => facets.length,
      { signal: params.signal, getTimings: () => timings, shouldReport: () => timings.request_ms !== undefined }
    )
  }

  private async queryFacets(
    {
      searchVerticalsOnly,
      mainCategorySlug,
      categorySlug,
      subCategorySlug,
      contentSlug,
      search,
      premium,
      level,
      signal,
    }: GetFacetsParams,
    timings: WebappLoadTimings
  ): Promise<string[]> {
    const preparedAt = performance.now()
    const queries = await this.buildQueries({
      mainCategorySlug,
      categorySlug,
      subCategorySlug,
      contentSlug,
      search,
      premium,
      level,
    })

    timings.preparation_ms = Math.round(performance.now() - preparedAt)
    // Currently, we support only single queries
    if (!queries.length || queries.length > 1) {
      return []
    }

    const baseFilter = queries[0].facetFilter
    const filter = searchVerticalsOnly
      ? [SEARCH_VERTICAL_FILTER, ...(Array.isArray(baseFilter) ? baseFilter : [baseFilter])]
      : baseFilter
    const q = queries[0].query.q

    // Get the most popular tags
    const index = client.index(indexName)
    if (features.compactCategoryNavigation) {
      const { facetDistribution } = await measureSearchRequest(timings, () =>
        index.search(
          q,
          {
            filter,
            facets: ['tags', 'vertical'],
            limit: 0,
            attributesToRetrieve: [],
          },
          { signal }
        )
      )
      return getRefiningTags(facetDistribution, MAX_ASSETS_FILTERS_TAGS)
    }

    const { facetHits } = await measureSearchRequest(timings, () =>
      index.searchForFacetValues(
        {
          facetQuery: '',
          facetName: 'tags',
          filter,
          q,
        },
        {
          signal,
        }
      )
    )

    return Object.values(facetHits)
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_ASSETS_FILTERS_TAGS)
      .map((facetHit) => facetHit.value)
  }

  /**
   * Resolves the Page Keys whose Page Priorities sort the request's page, most specific first: a
   * Content's own key and then its Subcategory's, or the single key of any other level. Empty when
   * the request has no category context (search results, the "ALL" tab) or a node is unknown.
   * @private
   */
  private async resolvePageKeys(
    mainCategorySlug?: string | null,
    categorySlug?: string | null,
    subCategorySlug?: string | null,
    contentSlug?: string | null
  ): Promise<string[]> {
    if (!mainCategorySlug) {
      return []
    }

    const scope = {
      mainCategoryIdOrSlug: mainCategorySlug,
      categoryIdOrSlug: categorySlug,
      subCategoryIdOrSlug: subCategorySlug,
    }

    if (categorySlug && subCategorySlug && contentSlug) {
      const content = await getContentByIdOrSlug(contentSlug, scope)
      if (!content) {
        return []
      }
      const subCategoryIds = [content.mainCategoryId, content.categoryId, content.subCategoryId]
      return [buildPageKey([...subCategoryIds, content.id]), buildPageKey(subCategoryIds)]
    }

    if (categorySlug && subCategorySlug) {
      const subCategory = await getSubCategoryByIdOrSlug(subCategorySlug, scope)
      return subCategory ? [buildPageKey([subCategory.mainCategoryId, subCategory.categoryId, subCategory.id])] : []
    }

    if (categorySlug) {
      const category = await getCategoryByIdOrSlug(categorySlug, scope)
      return category ? [buildPageKey([category.mainCategoryId, category.id])] : []
    }

    const mainCategory = await getMainCategoryByIdOrSlug(mainCategorySlug)
    return mainCategory ? [buildPageKey([mainCategory.id])] : []
  }

  /**
   * Builds the vertical type filter based on the category slug or main category slug.
   * If the category has a specific asset type, it will be used.
   * Otherwise, the main category slug will determine the vertical type.
   * @param mainCategorySlug
   * @param categorySlug
   * @private
   */
  private async buildVerticalFilter(mainCategorySlug?: string | null, categorySlug?: string | null): Promise<string> {
    const category: Category | null = categorySlug
      ? await getCategoryByIdOrSlug(categorySlug, {
          mainCategoryIdOrSlug: mainCategorySlug,
        })
      : null

    let vertical = category?.assetType ? assetTypeToMeilisearchVertical(category?.assetType) : null

    if (!vertical && mainCategorySlug) {
      vertical = mainCategorySlugToMeilisearchVertical(mainCategorySlug)
    }

    return Promise.resolve(vertical ? `vertical = "${vertical}"` : '')
  }

  private buildPremiumFilter(premium?: boolean): Promise<string[]> {
    return Promise.resolve(premium !== undefined ? [`level = ${premium ? 1 : 0}`] : [])
  }

  private buildLevelFilter(level?: number): Promise<string[]> {
    return Promise.resolve(level !== undefined ? [`level = ${level}`] : [])
  }

  private async buildQueries({
    mainCategorySlug,
    categorySlug,
    subCategorySlug,
    contentSlug,
    search,
    premium,
    level,
    popularityRankingPeriod,
    sort,
    tags,
    contentsLimit,
    includeMainCategoryVerticals,
    attributesToRetrieve,
  }: {
    mainCategorySlug?: string | null
    categorySlug?: string | null
    subCategorySlug?: string | null
    contentSlug?: string | null
    search?: string
    premium?: boolean
    level?: number
    popularityRankingPeriod?: DownloadsRankingPeriod
    sort?: SortFilterValue
    tags?: string[]
    contentsLimit?: number | null
    includeMainCategoryVerticals?: boolean
    attributesToRetrieve?: string[]
  }): Promise<ComposedQuery[]> {
    // Here we'll store the queries in case there's more than one
    const queries: ComposedQuery[] = []

    const query = search ? search.trim() : ''
    const verticalFilter =
      includeMainCategoryVerticals && mainCategorySlug === '3d' && !categorySlug
        ? 'vertical IN ["3d", "materials", "environments"]'
        : await this.buildVerticalFilter(mainCategorySlug, categorySlug)
    const premiumFilter = await this.buildPremiumFilter(premium)
    const levelFilter = await this.buildLevelFilter(level)
    const tagsFilter = (tags ?? [])
      .filter((tag) => !commonConnectors.includes(tag))
      .map((tag) => `tags = "${tag}"`)
      .join(' OR ')

    const sortQuery: string[] = buildSortQuery({
      sort,
      hasTextQuery: query.length > 0,
      perPageOrdering: PER_PAGE_ORDERING,
      pageKeys: PER_PAGE_ORDERING
        ? await this.resolvePageKeys(mainCategorySlug, categorySlug, subCategorySlug, contentSlug)
        : [],
      popularityRankingPeriod,
    })

    // Determine if we have enough information to resolve the content
    const shouldResolveContent =
      !!mainCategorySlug && !!categorySlug && (!!subCategorySlug || features.compactCategoryNavigation)

    if (shouldResolveContent) {
      // If we have more than one content, we need to add a query for each one
      let content: Content | null = null

      if (contentSlug) {
        content = await getContentByIdOrSlug(contentSlug, {
          mainCategoryIdOrSlug: mainCategorySlug,
          categoryIdOrSlug: categorySlug,
          subCategoryIdOrSlug: subCategorySlug,
        })
      }

      let contents: Content[] = content
        ? [content]
        : await getContents({
            mainCategoryIdOrSlug: mainCategorySlug,
            subCategoryIdOrSlug: subCategorySlug,
            categoryIdOrSlug: categorySlug,
          })

      if (contentsLimit && subCategorySlug) {
        console.debug('Limiting contents to', contentsLimit)
        contents = contents.slice(0, contentsLimit)
      }

      const parentTagFilters: string[] = []
      contents.forEach((content) => {
        // Contents with search tag
        if (content.searchTag) {
          const searchTagFilter = content.searchTag
            .replaceAll('+', ' ')
            .split(' ')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)
            .filter((tag) => !commonConnectors.includes(tag))
            .map((tag) => `tags IN ["${tag.toLowerCase()}"]`)
            .join(' AND ')
          if (searchTagFilter.length > 0) {
            if (!subCategorySlug) {
              parentTagFilters.push(`(${searchTagFilter})`)
              return
            }
            queries.push({
              query: {
                indexUid: indexName,
                q: query,
                filter: [verticalFilter, ...premiumFilter, ...levelFilter, searchTagFilter, tagsFilter].filter(
                  (filter) => filter.trim().length > 0
                ),
                sort: sortQuery,
                attributesToRetrieve,
              },
              facetFilter: [verticalFilter, ...premiumFilter, ...levelFilter, searchTagFilter].filter(
                (filter) => filter.trim().length > 0
              ),
            })
          }
        }
        // Contents with a search query
        else if (content.searchQuery && content.searchQuery.trim().length > 0) {
          queries.push({
            query: {
              indexUid: indexName,
              q: buildTextQuery(query, content.searchQuery),
              filter: [verticalFilter, ...premiumFilter, ...levelFilter, tagsFilter].filter(
                (filter) => filter.trim().length > 0
              ),
              sort: sortQuery,
              attributesToRetrieve,
            },
            facetFilter: [verticalFilter, ...premiumFilter, ...levelFilter].filter(
              (filter) => filter.trim().length > 0
            ),
          })
        }
      })
      if (parentTagFilters.length) {
        const categoryFilter = `(${Array.from(new Set(parentTagFilters)).join(' OR ')})`
        queries.push({
          query: {
            indexUid: indexName,
            q: query,
            filter: [verticalFilter, ...premiumFilter, ...levelFilter, categoryFilter, tagsFilter].filter(Boolean),
            sort: sortQuery,
            attributesToRetrieve,
          },
          facetFilter: [verticalFilter, ...premiumFilter, ...levelFilter, categoryFilter].filter(Boolean),
        })
      }
    }

    if (!queries.length) {
      // Default content search
      queries.push({
        query: {
          indexUid: indexName,
          q: query,
          filter: [verticalFilter, ...premiumFilter, ...levelFilter, tagsFilter].filter(
            (filter) => filter.trim().length > 0
          ),
          sort: sortQuery,
          attributesToRetrieve,
        },
        facetFilter: [verticalFilter, ...premiumFilter, ...levelFilter].filter((filter) => filter.trim().length > 0),
      })
    }

    return queries
  }
}
